import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Smartphone, Settings, Camera, RefreshCw, Trash2, Wifi, Download, HelpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';

const QrScannerHelpPage = () => {
  const navigate = useNavigate();

  const troubleshootingSteps = [
    {
      icon: Camera,
      title: 'Check Camera Permissions',
      content: (
        <div>
          <p className="font-semibold text-slate-200 mb-2">For iPhone:</p>
          <ol className="list-decimal list-inside space-y-1 text-slate-400">
            <li>Go to <span className="font-mono text-purple-300">Settings &gt; Privacy &gt; Camera</span>.</li>
            <li>Ensure the switch next to your web browser (e.g., Safari, Chrome) is turned ON.</li>
          </ol>
          <p className="font-semibold text-slate-200 mt-4 mb-2">For Android:</p>
          <ol className="list-decimal list-inside space-y-1 text-slate-400">
            <li>Go to <span className="font-mono text-purple-300">Settings &gt; Apps</span>.</li>
            <li>Find and tap on your web browser (e.g., Chrome).</li>
            <li>Tap on <span className="font-mono text-purple-300">Permissions &gt; Camera</span> and select "Allow".</li>
          </ol>
        </div>
      ),
    },
    {
      icon: RefreshCw,
      title: 'Restart the App/Browser',
      content: 'Completely close your web browser and relaunch it. This can clear temporary glitches that might be affecting the camera.',
    },
    {
      icon: Smartphone,
      title: 'Restart Your Device',
      content: 'A simple restart of your phone can resolve many unexpected issues by clearing the system\'s temporary memory.',
    },
    {
      icon: Trash2,
      title: 'Clear Browser Cache (Android)',
      content: (
        <div>
          <p className="mb-2">This can sometimes resolve issues caused by outdated data.</p>
          <ol className="list-decimal list-inside space-y-1 text-slate-400">
            <li>Go to <span className="font-mono text-purple-300">Settings &gt; Apps</span>.</li>
            <li>Find and tap on your web browser.</li>
            <li>Tap on <span className="font-mono text-purple-300">Storage &gt; Clear Cache</span>.</li>
          </ol>
        </div>
      ),
    },
    {
      icon: Wifi,
      title: 'Check Network Connection',
      content: 'While scanning itself is offline, navigating to the asset page requires a stable internet connection. Ensure you are connected to Wi-Fi or have mobile data.',
    },
    {
      icon: Download,
      title: 'Update Your Browser',
      content: 'Make sure your web browser is updated to the latest version from the App Store or Google Play Store to ensure compatibility with modern web features.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8 flex flex-col items-center">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-3xl"
      >
        <div className="w-full mb-4">
          <Button variant="ghost" onClick={() => navigate('/assets/scan')} className="text-slate-300 hover:bg-slate-700 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <Card className="glass-effect border-slate-700 shadow-2xl">
          <CardHeader className="text-center border-b border-slate-700 pb-6">
            <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-4 shadow-lg">
              <HelpCircle className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              QR Scanner Troubleshooting
            </CardTitle>
            <CardDescription className="text-slate-400 mt-2">
              Having trouble with the QR scanner? Follow these steps to resolve common issues.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
              {troubleshootingSteps.map((step, index) => (
                <AccordionItem value={`item-${index}`} key={index} className="border-slate-700">
                  <AccordionTrigger className="text-lg font-semibold text-slate-200 hover:no-underline hover:text-purple-400 transition-colors">
                    <div className="flex items-center">
                      <step.icon className="w-5 h-5 mr-4 text-blue-400" />
                      {step.title}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-300 pt-2 pb-4 px-2 text-base">
                    {step.content}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-8 p-4 bg-slate-800/50 border border-slate-700 rounded-lg text-center">
              <p className="text-slate-300">If you've tried all the steps and the issue persists, please contact our support team for further assistance.</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default QrScannerHelpPage;